/**
 * Hover state for the CATEGORICAL charts — the funnel, the ranked bars and the
 * donut.
 *
 * Per-shape hit-testing here, not the nearest-x snapping the line charts use,
 * and the difference is the charts themselves rather than a preference: a funnel
 * rung is 34 units tall and a donut segment is a whole arc, so a shape IS a
 * usable hover target. Nearest-x exists because a day on a 366-point line is
 * three pixels wide. Applying it to a donut would mean inventing an ordering for
 * things that have none.
 *
 * NO KEYBOARD PATH, and that is a deliberate difference from `useNearestPoint`
 * rather than an omission. The line charts are focusable because their per-day
 * figures exist nowhere else in the DOM. These three already render every label,
 * count and percentage as text in the list beside the chart — the SVG is the
 * thing carrying no text, which is exactly what the tooltip is for. Nothing here
 * is reachable by pointer only, so nothing here needs a second way in, and
 * adding one would put three to fourteen new tab stops on the page to re-read
 * text the reader is already on.
 */

import { type PointerEvent, useCallback, useState } from "react";

export interface HoverPoint {
  /** The hovered shape's index, or null. */
  index: number | null;
  /** The edge the tooltip pins to — always the half the pointer is NOT in. */
  side: "left" | "right";
  show(index: number, side: "left" | "right"): void;
  hide(): void;
}

/**
 * Which side to pin a tooltip to, given a pointer event over an element that
 * spans the chart's width.
 *
 * Returns the OPPOSITE half from the pointer, so the bubble never covers the
 * shape it describes. A zero-width box (not laid out, or jsdom, which reports
 * every rect as zero) falls back to `right`, which is where the bubble sits when
 * nothing better is known rather than an assertion about the pointer.
 */
export function pointerSide(event: PointerEvent<Element>): "left" | "right" {
  const box = event.currentTarget.getBoundingClientRect();
  if (box.width <= 0) return "right";
  return event.clientX - box.left > box.width / 2 ? "left" : "right";
}

export function useHoverPoint(): HoverPoint {
  const [state, setState] = useState<{ index: number; side: "left" | "right" } | null>(null);
  const show = useCallback((index: number, side: "left" | "right") => {
    setState({ index, side });
  }, []);
  const hide = useCallback(() => setState(null), []);
  return { index: state?.index ?? null, side: state?.side ?? "right", show, hide };
}
